/**
 * @fileoverview Generic field type definitions for glossary terms.
 * 
 * This module defines a flexible type system for representing different patterns
 * of content within glossary terms. Instead of having separate types for each
 * field pattern, we use generic discriminated unions that can describe any
 * structured content pattern used across the glossary.
 * 
 * This approach solves several challenges:
 * 1. Reduces type explosion as new fields are added
 * 2. Provides consistent runtime type checking
 * 3. Makes UI components more reusable
 * 4. Supports extension without code changes
 * 
 * @module resources/types/glossary/field-types
 * @description Provides type definitions for parsing YAML content into strongly-typed
 * TypeScript objects with clear structure identification to support dynamic UI rendering.
 * 
 * @exports ContentType - Enum of content structure patterns
 * @exports ContentTypeValue - Union type of all content type values
 * @exports DEFAULT_DISPLAY_CONFIG - Default display configurations
 * @exports DisplayConfig - Interface for UI rendering configuration
 * @exports ContentSection - Base interface for all content sections
 * @exports StringListSection - Section with simple string lists
 * @exports KeyValueListSection - Section with key-value pairs
 * @exports NestedListSection - Section with nested lists
 * @exports NameDescriptionPair - Common name/description pattern
 * @exports DefinitionListSection - Strongly-typed name/description section
 * @exports NameWithExamples - Common name/examples pattern
 * @exports GroupedExamplesSection - Strongly-typed name/examples section
 * @exports ContentSectionTypes - Union of all section types
 * @exports KeyValueSection - Type for key-value structured sections
 * @exports ValidationError - Error information from validation
 * @exports ValidationResult - Result of content validation
 * @exports ParsedContent - Container for all parsed sections
 * @exports ParserOptions - Options for YAML parsing
 */

// ============================================================================
// CONTENT TYPE DEFINITIONS
// ============================================================================

/**
 * Identifies the structural pattern of content
 * Used as a discriminator for determining UI rendering approach
 * 
 * @typedef {Object} ContentType
 * @property {string} STRING_LIST - Simple string list (examples, features, best practices)
 * @property {string} KEY_VALUE_LIST - Key-value pairs like name/description (types, objectives)
 * @property {string} NESTED_LIST - Items with nested lists (components with examples)
 */
export const ContentType = {
  /** Simple string list (examples, features, best practices) */
  STRING_LIST: 'STRING_LIST' as const,
  /** Key-value pairs like name/description (types, objectives) */
  KEY_VALUE_LIST: 'KEY_VALUE_LIST' as const,
  /** Items with nested lists (components with examples) */
  NESTED_LIST: 'NESTED_LIST' as const
} as const

/**
 * Union type of all possible content type values
 * Used for type discrimination
 * 
 * @typedef {string} ContentTypeValue
 */
export type ContentTypeValue = typeof ContentType[keyof typeof ContentType]

/**
 * Default display configuration for each content type
 * Provides sensible UI rendering defaults based on content structure
 * 
 * @typedef {Object} DefaultDisplayConfig
 * @property {Object} STRING_LIST - Configuration for string lists
 * @property {Object} KEY_VALUE_LIST - Configuration for key-value lists
 * @property {Object} NESTED_LIST - Configuration for nested lists
 */
export const DEFAULT_DISPLAY_CONFIG = {
  [ContentType.STRING_LIST]: {
    component: 'bullet-list',
    layout: {
      compact: true
    }
  },
  [ContentType.KEY_VALUE_LIST]: {
    component: 'definition-list',
    layout: {
      columns: 1
    }
  },
  [ContentType.NESTED_LIST]: {
    component: 'grouped-list',
    layout: {
      nestedStyle: 'indented'
    }
  }
} as const

/**
 * Display configuration options for UI rendering
 * Used to provide hints to UI components on how to render content
 * 
 * @interface DisplayConfig
 * @property {string} component - Suggested component to render this content
 * @property {Object} [layout] - Layout configuration hints
 *   @property {number} [layout.columns] - Number of columns for grid layouts
 *   @property {boolean} [layout.compact] - Whether items should be displayed in a compact form
 *   @property {string} [layout.nestedStyle] - How nested content should be styled
 *   @property {boolean} [layout.gridLayout] - Whether to use a grid layout
 * @property {string} [className] - Custom class names to apply
 * 
 * @example
 * ```typescript
 * const displayConfig: DisplayConfig = {
 *   component: 'definition-list',
 *   layout: {
 *     columns: 2,
 *     compact: true
 *   },
 *   className: 'custom-section'
 * }
 * ```
 */
export interface DisplayConfig {
  /** Suggested component to render this content */
  component: string
  /** Layout configuration hints */
  layout?: {
    /** Number of columns for grid layouts */
    columns?: number
    /** Whether items should be displayed in a compact form */
    compact?: boolean
    /** How nested content should be styled */
    nestedStyle?: 'indented' | 'cards' | 'tabs'
    /** Whether to use a grid layout */
    gridLayout?: boolean
  }
  /** Custom class names to apply */
  className?: string
}

/**
 * Base interface for all content sections
 * Contains common properties and type discrimination
 * 
 * @interface ContentSection
 * @template T - The type of items in this section
 * @property {ContentTypeValue} type - Discriminator field for type checking
 * @property {string} sectionName - The section name from the YAML
 * @property {T[]} items - The actual content items with proper typing
 * @property {DisplayConfig} display - Display configuration for UI rendering
 *  @property {string} display.component - The component to render this section
 *  @property {Object} display.layout - Layout configuration hints
 *    @property {number} [display.layout.columns] - Number of columns for grid layouts
 *    @property {boolean} [display.layout.compact] - Whether items should be displayed in a compact form
 *    @property {string} [display.layout.nestedStyle] - How nested content should be styled
 *    @property {boolean} [display.layout.gridLayout] - Whether to use a grid layout
 *  @property {string} [display.className] - Custom class names to apply
 * 
 * @example
 * ```typescript
 * const section: ContentSection<string> = {
 *   type: ContentType.STRING_LIST,
 *   sectionName: 'examples',
 *   items: ['Example 1', 'Example 2'],
 *   display: {
 *     component: 'bullet-list',
 *     layout: { compact: true }
 *   }
 * }
 * ```
 */
export interface ContentSection<T> {
  /** Discriminator field for type checking */
  readonly type: ContentTypeValue
  /** The section name from the YAML (e.g., "examples", "types") */
  readonly sectionName: string
  /** The actual content items with proper typing */
  items: ReadonlyArray<T>
  /** Display configuration for UI rendering */
  display: DisplayConfig
}

// ============================================================================
// SPECIFIC CONTENT TYPES
// ============================================================================

/**
 * A section containing a simple list of strings
 * Common examples: features, examples, bestPractices
 * 
 * @interface StringListSection
 * @extends {ContentSection<string>}
 * @property {typeof ContentType.STRING_LIST} type - Discriminator field identifying this as a string list
 * @property {string} sectionName - The section name from the YAML
 * @property {ReadonlyArray<string>} items - List of string items
 * @property {DisplayConfig} display - Display configuration for UI rendering
 *  @property {string} display.component - The component to render this section
 *  @property {Object} display.layout - Layout configuration hints
 *    @property {number} [display.layout.columns] - Number of columns for grid layouts
 *    @property {boolean} [display.layout.compact] - Whether items should be displayed in a compact form
 *    @property {string} [display.layout.nestedStyle] - How nested content should be styled
 *    @property {boolean} [display.layout.gridLayout] - Whether to use a grid layout
 *  @property {string} [display.className] - Custom class names to apply
 * 
 * @example
 * ```yaml
 * examples:
 *   - "Creating personalized content and experiences for target accounts"
 *   - "Coordinating multi-channel engagement across account buying teams"
 * ```
 * 
 * @example
 * ```typescript
 * const examplesSection: StringListSection = {
 *   type: ContentType.STRING_LIST,
 *   sectionName: 'examples',
 *   items: [
 *     "Creating personalized content and experiences for target accounts",
 *     "Coordinating multi-channel engagement across account buying teams"
 *   ],
 *   display: {
 *     component: 'bullet-list',
 *     layout: { compact: true }
 *   }
 * }
 * ```
 */
export interface StringListSection extends ContentSection<string> {
  readonly type: typeof ContentType.STRING_LIST
  items: ReadonlyArray<string>
}

/**
 * A section containing items with key-value pairs
 * Common examples: types, objectives, timeWindows
 * 
 * @interface KeyValueListSection
 * @template T - The key structure of each item
 * @extends {ContentSection<T>}
 * @property {typeof ContentType.KEY_VALUE_LIST} type - Discriminator field identifying this as a key-value list
 * @property {string} sectionName - The section name from the YAML
 * @property {ReadonlyArray<T>} items - Array of items with the structure defined by T
 * @property {Object} keyInfo - Metadata about the structure of items
 *   @property {string} keyInfo.primaryKey - The primary display key (e.g., "name")
 *   @property {string} keyInfo.secondaryKey - The secondary content key (e.g., "description")
 *   @property {ReadonlyArray<string>} keyInfo.allKeys - All available keys in the items
 * @property {DisplayConfig} display - Display configuration for UI rendering
 *  @property {string} display.component - The component to render this section
 *  @property {Object} display.layout - Layout configuration hints
 *    @property {number} [display.layout.columns] - Number of columns for grid layouts
 *    @property {boolean} [display.layout.compact] - Whether items should be displayed in a compact form
 *    @property {string} [display.layout.nestedStyle] - How nested content should be styled
 *    @property {boolean} [display.layout.gridLayout] - Whether to use a grid layout
 *  @property {string} [display.className] - Custom class names to apply
 * 
 * @example
 * ```yaml
 * types:
 *   - name: "One-to-One ABM"
 *     description: "Highly personalized campaigns for individual accounts"
 * ```
 * 
 * @example
 * ```typescript
 * const typesSection: KeyValueListSection<{name: string, description: string}> = {
 *   type: ContentType.KEY_VALUE_LIST,
 *   sectionName: 'types',
 *   items: [
 *     {
 *       name: "One-to-One ABM",
 *       description: "Highly personalized campaigns for individual accounts"
 *     }
 *   ],
 *   keyInfo: {
 *     primaryKey: 'name',
 *     secondaryKey: 'description',
 *     allKeys: ['name', 'description']
 *   },
 *   display: {
 *     component: 'definition-list',
 *     layout: { columns: 1 }
 *   }
 * }
 * ```
 */
export interface KeyValueListSection<T extends Record<string, string>> extends ContentSection<T> {
  readonly type: typeof ContentType.KEY_VALUE_LIST
  items: ReadonlyArray<T>
  /** Metadata about the structure of items */
  keyInfo: Readonly<{
    /** The primary display key (e.g., "name") */
    primaryKey: string
    /** The secondary content key (e.g., "description") */
    secondaryKey: string
    /** All available keys in the items */
    allKeys: ReadonlyArray<string>
  }>
}

/**
 * A section containing items with a main property and a nested string array
 * Common examples: components with examples, channels with items
 * 
 * @interface NestedListSection
 * @template MainKey - The key for the main property (usually "name")
 * @template ListKey - The key for the nested list property (usually "examples")
 * @extends {ContentSection<Record<MainKey | ListKey, string | ReadonlyArray<string>>>}
 * @property {typeof ContentType.NESTED_LIST} type - Discriminator field identifying this as a nested list
 * @property {string} sectionName - The section name from the YAML
 * @property {ReadonlyArray<Record<MainKey, string> & Record<ListKey, ReadonlyArray<string>>>} items - Array of items with main property and nested list
 * @property {Object} keyInfo - Metadata about the structure of items
 *   @property {MainKey} keyInfo.mainKey - The main property key name (e.g., "name")
 *   @property {ListKey} keyInfo.listKey - The nested list property key name (e.g., "examples")
 * @property {DisplayConfig} display - Display configuration for UI rendering
 *  @property {string} display.component - The component to render this section
 *  @property {Object} display.layout - Layout configuration hints
 *    @property {number} [display.layout.columns] - Number of columns for grid layouts
 *    @property {boolean} [display.layout.compact] - Whether items should be displayed in a compact form
 *    @property {string} [display.layout.nestedStyle] - How nested content should be styled
 *    @property {boolean} [display.layout.gridLayout] - Whether to use a grid layout
 *  @property {string} [display.className] - Custom class names to apply
 * 
 * @example
 * ```yaml
 * components:
 *   - name: "Emotional Triggers"
 *     examples: ["Humor", "Surprise", "Inspiration", "Controversy"]
 * ```
 * 
 * @example
 * ```typescript
 * const componentsSection: NestedListSection<'name', 'examples'> = {
 *   type: ContentType.NESTED_LIST,
 *   sectionName: 'components',
 *   items: [
 *     {
 *       name: "Emotional Triggers",
 *       examples: ["Humor", "Surprise", "Inspiration", "Controversy"]
 *     }
 *   ],
 *   keyInfo: {
 *     mainKey: 'name',
 *     listKey: 'examples'
 *   },
 *   display: {
 *     component: 'grouped-list',
 *     layout: { nestedStyle: 'indented' }
 *   }
 * }
 * ```
 */
export interface NestedListSection<
  MainKey extends string = string,
  ListKey extends string = string
> extends ContentSection<Record<MainKey | ListKey, string | ReadonlyArray<string>>> {
  readonly type: typeof ContentType.NESTED_LIST
  items: ReadonlyArray<Record<MainKey, string> & Record<ListKey, ReadonlyArray<string>>>
  /** Metadata about the structure of items */
  keyInfo: Readonly<{
    /** The main property key name (e.g., "name") */
    mainKey: MainKey
    /** The nested list property key name (e.g., "examples") */
    listKey: ListKey
  }>
}

// ============================================================================
// COMMON CONTENT PATTERNS
// ============================================================================

/**
 * The common name/description pair pattern
 * Used in many sections like types, timeWindows, objectives
 * 
 * @interface NameDescriptionPair
 * @property {string} name - The name or title of the item
 * @property {string} description - The description or definition of the item
 * 
 * @example
 * ```typescript
 * const item: NameDescriptionPair = {
 *   name: "One-to-One ABM",
 *   description: "Highly personalized campaigns for individual accounts"
 * }
 * ```
 */
export interface NameDescriptionPair {
  /** The name or title of the item */
  name: string
  /** The description or definition of the item */
  description: string
}

/**
 * A strongly-typed section containing name/description pairs
 * Common examples: types, objectives, timeWindows
 * 
 * @interface DefinitionListSection
 * @extends {ContentSection<NameDescriptionPair>}
 * @extends {Omit<KeyValueListSection<Record<string, string>>, 'items' | 'keyInfo'>}
 * @property {typeof ContentType.KEY_VALUE_LIST} type - Discriminator field identifying this as a key-value list
 * @property {string} sectionName - The section name from the YAML
 * @property {ReadonlyArray<NameDescriptionPair>} items - Array of name/description pairs
 * @property {Object} keyInfo - Metadata about the structure of items
 *   @property {'name'} keyInfo.primaryKey - The primary key (always "name")
 *   @property {'description'} keyInfo.secondaryKey - The secondary key (always "description")
 *   @property {ReadonlyArray<'name' | 'description'>} keyInfo.allKeys - All available keys
 * @property {DisplayConfig} display - Display configuration for UI rendering
 *   @property {string} display.component - The component to render this section
 *   @property {Object} display.layout - Layout configuration hints
 *     @property {number} [display.layout.columns] - Number of columns for grid layouts
 *     @property {boolean} [display.layout.compact] - Whether items should be displayed in a compact form
 *     @property {string} [display.layout.nestedStyle] - How nested content should be styled
 *     @property {boolean} [display.layout.gridLayout] - Whether to use a grid layout
 *   @property {string} [display.className] - Custom class names to apply
 * 
 * @example
 * ```yaml
 * types:
 *   - name: "One-to-One ABM"
 *     description: "Highly personalized campaigns for individual accounts"
 *   - name: "One-to-Few ABM"
 *     description: "Targeted campaigns for small groups of similar accounts"
 * ```
 * 
 * @example
 * ```typescript
 * const typesSection: DefinitionListSection = {
 *   type: ContentType.KEY_VALUE_LIST,
 *   sectionName: 'types',
 *   items: [
 *     {
 *       name: "One-to-One ABM",
 *       description: "Highly personalized campaigns for individual accounts"
 *     },
 *     {
 *       name: "One-to-Few ABM",
 *       description: "Targeted campaigns for small groups of similar accounts"
 *     }
 *   ],
 *   keyInfo: {
 *     primaryKey: 'name',
 *     secondaryKey: 'description',
 *     allKeys: ['name', 'description']
 *   },
 *   display: {
 *     component: 'definition-list',
 *     layout: { columns: 1 }
 *   }
 * }
 * ```
 */
export interface DefinitionListSection extends ContentSection<NameDescriptionPair>, 
  Omit<KeyValueListSection<Record<string, string>>, 'items' | 'keyInfo'> {
  readonly type: typeof ContentType.KEY_VALUE_LIST
  items: ReadonlyArray<NameDescriptionPair>
  keyInfo: Readonly<{
    primaryKey: 'name'
    secondaryKey: 'description'
    allKeys: ReadonlyArray<'name' | 'description'>
  }>
}

/**
 * The common name/examples pair pattern
 * Used in sections like components, channels
 * 
 * @interface NameWithExamples
 * @property {string} name - The name or category title
 * @property {ReadonlyArray<string>} examples - List of example items
 * 
 * @example
 * ```typescript
 * const item: NameWithExamples = {
 *   name: "Emotional Triggers",
 *   examples: ["Humor", "Surprise", "Inspiration", "Controversy"]
 * }
 * ```
 */
export interface NameWithExamples {
  /** The name or category title */
  name: string
  /** List of example items */
  examples: ReadonlyArray<string>
}

/**
 * A strongly-typed section containing items with examples
 * Common examples: components, channels
 * 
 * @interface GroupedExamplesSection
 * @extends {NestedListSection<'name', 'examples'>}
 * @property {typeof ContentType.NESTED_LIST} type - Discriminator field identifying this as a nested list
 * @property {string} sectionName - The section name from the YAML
 * @property {ReadonlyArray<NameWithExamples>} items - Array of items with name and examples
 * @property {Object} keyInfo - Metadata about the structure of items
 *   @property {'name'} keyInfo.mainKey - The main key (always "name")
 *   @property {'examples'} keyInfo.listKey - The list key (always "examples")
 * @property {DisplayConfig} display - Display configuration for UI rendering
 *   @property {string} display.component - The component to render this section
 *   @property {Object} display.layout - Layout configuration hints
 *     @property {number} [display.layout.columns] - Number of columns for grid layouts
 *     @property {boolean} [display.layout.compact] - Whether items should be displayed in a compact form
 *     @property {string} [display.layout.nestedStyle] - How nested content should be styled
 *     @property {boolean} [display.layout.gridLayout] - Whether to use a grid layout
 *   @property {string} [display.className] - Custom class names to apply
 * 
 * @example
 * ```yaml
 * components:
 *   - name: "Emotional Triggers"
 *     examples: ["Humor", "Surprise", "Inspiration", "Controversy"]
 *   - name: "Social Proof"
 *     examples: ["Testimonials", "Case Studies", "Reviews", "User Statistics"]
 * ```
 * 
 * @example
 * ```typescript
 * const componentsSection: GroupedExamplesSection = {
 *   type: ContentType.NESTED_LIST,
 *   sectionName: 'components',
 *   items: [
 *     {
 *       name: "Emotional Triggers",
 *       examples: ["Humor", "Surprise", "Inspiration", "Controversy"]
 *     },
 *     {
 *       name: "Social Proof",
 *       examples: ["Testimonials", "Case Studies", "Reviews", "User Statistics"]
 *     }
 *   ],
 *   keyInfo: {
 *     mainKey: 'name',
 *     listKey: 'examples'
 *   },
 *   display: {
 *     component: 'grouped-list',
 *     layout: { nestedStyle: 'indented' }
 *   }
 * }
 * ```
 */
export interface GroupedExamplesSection extends NestedListSection<'name', 'examples'> {
  readonly type: typeof ContentType.NESTED_LIST
  items: ReadonlyArray<NameWithExamples>
  keyInfo: Readonly<{
    mainKey: 'name'
    listKey: 'examples'
  }>
}

/**
 * Union type of all possible content section types
 * Used for type discrimination and generic handling
 * 
 * @typedef {StringListSection|KeyValueListSection<Record<string, string>>|NestedListSection<string, string>|DefinitionListSection|GroupedExamplesSection} ContentSectionTypes
 */
export type ContentSectionTypes = 
  | StringListSection
  | KeyValueListSection<Record<string, string>>
  | NestedListSection<string, string>
  | DefinitionListSection
  | GroupedExamplesSection

/**
 * Type for any section that has key-value structure
 * 
 * @typedef {KeyValueListSection<Record<string, string>> | DefinitionListSection} KeyValueSection
 * @see {KeyValueListSection} for the expected structure
 * @see {DefinitionListSection} for the expected structure
 */
export type KeyValueSection = KeyValueListSection<Record<string, string>> | DefinitionListSection

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Type guard for string list sections
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a string list
 * @see {StringListSection} for the expected structure
 * 
 * @example
 * ```typescript
 * if (isStringListSection(section)) {
 *   // section is now typed as StringListSection
 *   section.items.forEach(item => console.log(item))
 * }
 * ```
 */
export function isStringListSection(
  section: ContentSectionTypes
): section is StringListSection {
  return section.type === ContentType.STRING_LIST
}

/**
 * Type guard for key-value list sections
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a key-value list
 * @see {KeyValueListSection} for the expected structure
 * 
 * @example
 * ```typescript
 * if (isKeyValueListSection(section)) {
 *   // section is now typed as KeyValueListSection<Record<string, string>>
 *   const primaryKey = section.keyInfo.primaryKey
 *   section.items.forEach(item => console.log(item[primaryKey]))
 * }
 * ```
 */
export function isKeyValueListSection(
  section: ContentSectionTypes
): section is KeyValueListSection<Record<string, string>> {
  return section.type === ContentType.KEY_VALUE_LIST
}

/**
 * Type guard for nested list sections
 * 
 * @template MainKey - The expected main key type
 * @template ListKey - The expected list key type
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a nested list
 * @see {NestedListSection} for the expected structure
 * 
 * @example
 * ```typescript
 * if (isNestedListSection(section)) {
 *   // section is now typed as NestedListSection<string, string>
 *   const { mainKey, listKey } = section.keyInfo
 *   section.items.forEach(item => {
 *     console.log(item[mainKey])
 *     item[listKey].forEach(example => console.log(`- ${example}`))
 *   })
 * }
 * ```
 */
export function isNestedListSection<MainKey extends string, ListKey extends string>(
  section: ContentSectionTypes
): section is NestedListSection<MainKey, ListKey> {
  return section.type === ContentType.NESTED_LIST
}

/**
 * Type guard specifically for definition lists (name/description)
 * Validates both type and key structure
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a definition list
 * @see {DefinitionListSection} for the expected structure
 * 
 * @example
 * ```typescript
 * if (isDefinitionListSection(section)) {
 *   // section is now typed as DefinitionListSection
 *   section.items.forEach(item => {
 *     console.log(`${item.name}: ${item.description}`)
 *   })
 * }
 * ```
 */
export function isDefinitionListSection(
  section: ContentSectionTypes
): section is DefinitionListSection {
  return (
    section.type === ContentType.KEY_VALUE_LIST &&
    'keyInfo' in section &&
    section.keyInfo.primaryKey === 'name' &&
    section.keyInfo.secondaryKey === 'description'
  )
}

/**
 * Type guard specifically for grouped examples sections (name/examples)
 * Validates both type and key structure
 * 
 * @param {ContentSectionTypes} section - The content section to check
 * @returns {boolean} True if the section is a grouped examples list
 * @see {GroupedExamplesSection} for the expected structure
 * 
 * @example
 * ```typescript
 * if (isGroupedExamplesSection(section)) {
 *   // section is now typed as GroupedExamplesSection
 *   section.items.forEach(item => {
 *     console.log(`${item.name}:`)
 *     item.examples.forEach(example => console.log(`- ${example}`))
 *   })
 * }
 * ```
 */
export function isGroupedExamplesSection(
  section: ContentSectionTypes
): section is GroupedExamplesSection {
  return (
    section.type === ContentType.NESTED_LIST &&
    'keyInfo' in section &&
    section.keyInfo.mainKey === 'name' &&
    section.keyInfo.listKey === 'examples'
  )
}

// ============================================================================
// VALIDATION TYPES
// ============================================================================

/**
 * Error information from content validation
 * Provides structured format for validation errors
 * 
 * @interface ValidationError
 * @property {string} message - Error message
 * @property {string} path - Path to the error (e.g., "items[0].name")
 * @property {'error'|'warning'|'info'} severity - Severity level
 * 
 * @example
 * ```typescript
 * const error: ValidationError = {
 *   message: "Required field 'name' is missing",
 *   path: "items[2]",
 *   severity: 'error'
 * }
 * ```
 */
export interface ValidationError {
  /** Error message */
  message: string
  /** Path to the error (e.g., "items[0].name") */
  path: string
  /** Severity level */
  severity: 'error' | 'warning' | 'info'
}

/**
 * Result of a content validation
 * Contains any errors found during validation
 * 
 * @interface ValidationResult
 * @property {boolean} isValid - Whether the content is valid
 * @property {ReadonlyArray<ValidationError>} errors - Any validation errors found
 * 
 * @example
 * ```typescript
 * const result: ValidationResult = {
 *   isValid: false,
 *   errors: [
 *     {
 *       message: "Required field 'name' is missing",
 *       path: "items[2]",
 *       severity: 'error'
 *     }
 *   ]
 * }
 * ```
 */
export interface ValidationResult {
  /** Whether the content is valid */
  isValid: boolean
  /** Any validation errors found */
  errors: ReadonlyArray<ValidationError>
}

/**
 * Container for all parsed content sections
 * Provides type-safe accessors for different content patterns
 * 
 * @interface ParsedContent
 * @property {ReadonlyMap<string, ContentSectionTypes>} sections - Collection of all parsed sections by name
 * @property {(name: string) => ContentSectionTypes | undefined} getSection - Get a section with any type
 * @property {(name: string) => StringListSection | undefined} getStringList - Get a string list section
 * @property {<T extends Record<string, string>>(name: string) => KeyValueListSection<T> | undefined} getKeyValueList - Get a key-value list section with specific key structure
 * @property {<MainKey extends string = string, ListKey extends string = string>(name: string) => NestedListSection<MainKey, ListKey> | undefined} getNestedList - Get a nested list section with specific key structure
 * @property {(name: string) => DefinitionListSection | undefined} getDefinitionList - Get a definition list section (name/description pattern)
 * @property {(name: string) => GroupedExamplesSection | undefined} getGroupedExamples - Get a grouped examples section (name/examples pattern)
 * @property {() => ReadonlyMap<string, ValidationResult>} validate - Validate all sections
 * 
 * @example
 * ```typescript
 * // Access sections with type safety
 * const examplesSection = parsedContent.getStringList('examples')
 * const typesSection = parsedContent.getDefinitionList('types')
 * const componentsSection = parsedContent.getGroupedExamples('components')
 * 
 * // Validate all content
 * const validationResults = parsedContent.validate()
 * ```
 */
export interface ParsedContent {
  /** Collection of all parsed sections by name */
  sections: ReadonlyMap<string, ContentSectionTypes>

  /**
   * Get a section with any type
   * @param {string} name - The section name
   * @returns {ContentSectionTypes|undefined} The section or undefined if not found
   */
  getSection(name: string): ContentSectionTypes | undefined

  /**
   * Get a string list section
   * @param {string} name - The section name
   * @returns {StringListSection|undefined} The section or undefined if not found or wrong type
   */
  getStringList(name: string): StringListSection | undefined

  /**
   * Get a key-value list section with specific key structure
   * @template T - The expected key structure
   * @param {string} name - The section name
   * @returns {KeyValueListSection<T>|undefined} The section or undefined if not found or wrong type
   */
  getKeyValueList<T extends Record<string, string>>(name: string): KeyValueListSection<T> | undefined

  /**
   * Get a nested list section with specific key structure
   * @template MainKey - The main property key name
   * @template ListKey - The nested list property key name
   * @param {string} name - The section name
   * @returns {NestedListSection<MainKey, ListKey>|undefined} The section or undefined if not found or wrong type
   */
  getNestedList<MainKey extends string = string, ListKey extends string = string>(
    name: string
  ): NestedListSection<MainKey, ListKey> | undefined

  /**
   * Get a definition list section (name/description pattern)
   * @param {string} name - The section name
   * @returns {DefinitionListSection|undefined} The section or undefined if not found or wrong type
   */
  getDefinitionList(name: string): DefinitionListSection | undefined

  /**
   * Get a grouped examples section (name/examples pattern)
   * @param {string} name - The section name
   * @returns {GroupedExamplesSection|undefined} The section or undefined if not found or wrong type
   */
  getGroupedExamples(name: string): GroupedExamplesSection | undefined

  /**
   * Validate all sections
   * @returns {ReadonlyMap<string, ValidationResult>} Map of section names to validation results
   */
  validate(): ReadonlyMap<string, ValidationResult>
}

/**
 * Options for parsing YAML content
 * 
 * @interface ParserOptions
 * @property {boolean} [trimStrings=true] - Whether to trim string values
 * @property {Record<string, ContentTypeValue>} [sectionTypes] - Known section type mappings
 * @property {Record<string, Partial<DisplayConfig>>} [displayConfigs] - Custom display configurations for specific sections
 * 
 */
export interface ParserOptions {
  /** Whether to trim string values */
  trimStrings?: boolean
  /** Known section type mappings */
  sectionTypes?: Record<string, ContentTypeValue>
  /** Custom display configurations for specific sections */
  displayConfigs?: Record<string, Partial<DisplayConfig>>
}

/**
 * Field definition that maps a field name to its expected content pattern.
 * This is used both for validation during parsing and to guide UI rendering.
 * 
 * @interface GlossaryDynamicFieldDefinition
 * @property {string} fieldName - The field name as it appears in the term data
 * @property {string} displayName - Human-readable name for UI display
 * @property {string} tabId - The tab ID used for navigation
 * @property {ContentTypeValue} expectedType - Expected content pattern type
 * @property {string} [colorClass] - CSS class for styling (optional)
 */
export interface GlossaryDynamicFieldDefinition {
  /** The field name as it appears in the term data */
  readonly fieldName: string
  
  /** Human-readable name for UI display */
  readonly displayName: string
  
  /** The tab ID used for navigation */
  readonly tabId: string
  
  /** 
   * Expected content pattern - this is not a requirement but a hint
   * for the parser about what format we expect. The parser will still
   * handle unexpected formats but may log warnings.
   */
  readonly expectedType: ContentTypeValue
  
  /** CSS class for styling (optional) */
  readonly colorClass?: string
}

/**
 * Registry of field definitions for all supported glossary fields.
 * Each entry describes the expected structure and rendering information
 * for a specific field.
 * 
 * Adding a new field type is as simple as adding a new entry to this array.
 * 
 * @type {readonly GlossaryDynamicFieldDefinition[]}
 * 
 * Full list of field definitions:
 * advantages, limitations, components, types, stages, timeWindows, channels, bestPractices, objectives, features, successFactors, targeting
 */
export const glossaryFieldDefinitions: readonly GlossaryDynamicFieldDefinition[] = [
  {
    fieldName: 'advantages',
    displayName: 'Advantages',
    tabId: 'advantages',
    expectedType: ContentType.STRING_LIST,
    colorClass: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
  },
  {
    fieldName: 'limitations',
    displayName: 'Limitations',
    tabId: 'limitations',
    expectedType: ContentType.STRING_LIST,
    colorClass: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
  },
  {
    fieldName: 'components',
    displayName: 'Components',
    tabId: 'components',
    expectedType: ContentType.KEY_VALUE_LIST, // @NOTE: This is BOTH a key-value list and a nested list
    colorClass: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
  },
  {
    fieldName: 'types',
    displayName: 'Types',
    tabId: 'types',
    expectedType: ContentType.KEY_VALUE_LIST,
    colorClass: 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
  },
  {
    fieldName: 'stages',
    displayName: 'Stages',
    tabId: 'stages',
    expectedType: ContentType.KEY_VALUE_LIST,
    colorClass: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400'
  },
  {
    fieldName: 'timeWindows',
    displayName: 'Time Windows',
    tabId: 'timeWindows',
    expectedType: ContentType.KEY_VALUE_LIST,
    colorClass: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
  },
  {
    fieldName: 'channels',
    displayName: 'Channels',
    tabId: 'channels',
    expectedType: ContentType.KEY_VALUE_LIST,
    colorClass: 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400'
  },
  {
    fieldName: 'bestPractices',
    displayName: 'Best Practices',
    tabId: 'bestPractices',
    expectedType: ContentType.STRING_LIST,
    colorClass: 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400'
  },
  {
    fieldName: 'objectives',
    displayName: 'Objectives',
    tabId: 'objectives',
    expectedType: ContentType.STRING_LIST,
    colorClass: 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
  },
  {
    fieldName: 'features',
    displayName: 'Features',
    tabId: 'features',
    expectedType: ContentType.STRING_LIST,
    colorClass: 'bg-violet-50 text-violet-600 dark:bg-violet-900/20 dark:text-violet-400'
  },
  {
    fieldName: 'successFactors',
    displayName: 'Success Factors',
    tabId: 'successFactors',
    expectedType: ContentType.STRING_LIST,
    colorClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400'
  },
  {
    fieldName: 'targeting',
    displayName: 'Targeting',
    tabId: 'targeting',
    expectedType: ContentType.STRING_LIST,
    colorClass: 'bg-orange-50 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400'
  }
] as const